import {Component, Input, OnInit, QueryList, ViewChildren} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {ApiConfigurationService} from '@services/administration/machine/trigger/api-configuration.service';

import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';

import {ApiConfiguration} from '@models/machine/api-configuration';
import {ApiConfigurationDetails} from '@models/machine/api-configuration-details';
import {Machine} from '@models/machine/machine';
import {
  ApiConfigurationEditComponent
} from '@components/administration/machine/machine-configuration/trigger/api-configuration/modals/api-configuration-edit/api-configuration-edit.component';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-admin-machine-api-configuration',
    templateUrl: './api-configuration.component.html',
    styleUrls: ['./api-configuration.component.scss'],
    standalone: false
})
export class ApiConfigurationComponent implements OnInit {

  @Input()
  machine: Machine = new Machine();

  @Input()
  userRole: Role = new Role(new User());

  apiConfigs: ApiConfigurationDetails[] = [];

  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();
  private CURRENT_PAGE_NAME = 'ApiConfigurationComponent';

  constructor(
      private loadingIndicator: LoadingIndicatorService,
      private translation: TranslateService,
      private notificationService: NotificationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private apiConfigurationService: ApiConfigurationService,
      private modalService: ModalService) {
  }

  ngOnInit(): void {
    this.setApiEndpoints();
  }

  onAddEndpoint(): void {
    const modalRef = this.modalService.open(ApiConfigurationEditComponent, {size: 'lg'});
    modalRef.componentInstance.machine = this.machine;
    modalRef.componentInstance.apiConfigs = this.apiConfigs;
    modalRef.componentInstance.userRole = this.userRole;
    modalRef.result.then((result: ApiConfiguration) => {
      this.apiConfigs = [...result.apiConfigs];
    });
  }

  public onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.apiConfigs = this.tableHeaderSortableService.sortItems<ApiConfigurationDetails>(this.apiConfigs, column, direction);
  }

  public getEndpointUrl(apiConfigurationDetails: ApiConfigurationDetails): string {
    return this.apiConfigurationService.getEndpointUrl(this.machine, apiConfigurationDetails.url);
  }

  public getBadgeClass(isActive: boolean | null): string {
    switch (isActive) {
      case true:
        return 'badge-success';
      case false:
      case null:
        return 'badge-danger';
      default:
        return '';
    }
  }

  onEndpointUpdated(apiConfiguration: ApiConfiguration): void {
    this.apiConfigs = apiConfiguration.apiConfigs;
  }

  onApiConfigsUpdate(apiConfigs: ApiConfigurationDetails[]): void {
    this.apiConfigs = [...apiConfigs];
  }

  getStatus(value: boolean | null): string {
    switch (value) {
      case true:
        return 'Active';
      case false:
        return 'Inactive';
      default:
        return '';
    }
  }

  private setApiEndpoints(): void {
    if (this.machine.id !== undefined) {
      this.loadingIndicator.show(this.translation.instant('Loading'));
      this.apiConfigurationService.list(this.machine.id).subscribe({
        next: (apiEndpoints: ApiConfiguration) => {
          if (!apiEndpoints || !apiEndpoints.apiConfigs) {
            this.apiConfigs = [];
          } else {
            this.apiConfigs = apiEndpoints.apiConfigs;
          }
          this.loadingIndicator.hide();
        }, error: error => {
          console.log(error);
          this.loadingIndicator.hide();
          const message = this.translation.instant('Error getting list of ApiConfig');
          this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    }
  }

}
